import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { statusInk, statusPillClass, type StatusTone } from '@/lib/status';

// The status components. The vocabulary — the five tones and what each
// means — and the class helpers live in lib/status.ts.

/**
 * A state as a chip: one shape everywhere — full round, 12px medium, a 12px
 * icon when it has one. A pill is a state; a fact with no state is plain
 * muted text, not a neutral pill, unless it sits among pills as a kind.
 */
export function StatusPill({ tone = 'neutral', icon, className, children, ...rest }: {
  tone?: StatusTone;
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
} & Omit<ComponentProps<'span'>, 'children'>) {
  // `rest` carries ref and the trigger handlers a wrapping Hint (Radix
  // asChild) hands its child — without them the tooltip never opens.
  return (
    <span {...rest} className={cn(statusPillClass(tone), className)} data-tone={tone}>
      {icon}
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}

/** A state as text, in the tone's ink — the journey list's next action, a report line. */
export function StatusText({ tone = 'neutral', className, children }: {
  tone?: StatusTone;
  className?: string;
  children: ReactNode;
}) {
  return <span className={cn(statusInk(tone), tone !== 'neutral' && 'font-medium', className)}>{children}</span>;
}
